import React from 'react';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown-now';
import A11yFontIcon from 'components/A11yFontIcon';
import GameIcon from 'components/GameIcon';
import Ranking, { DISPLAY_FINAL } from 'components/Ranking';
import { padNumber } from 'utils/formatting';
import { TOURNAMENT_STATUS_UPCOMING } from 'utils/tournaments';
import ExclusiveCoverImg from 'resources/exclusive-tournament-game-cover.jpg';
import './TournamentMessage.css';

const countdownRenderer = ({ days, hours, minutes, seconds }) => {
  let output = '';
  if (days) {
    output += padNumber(days) + ':';
  }
  if (days || hours) {
    output += padNumber(hours) + ':';
  }
  output += padNumber(minutes) + ':' + padNumber(seconds);
  return <div className="countdown notranslate">{output}</div>;
};

export default function TournamentMessage({
  rankings,
  balanceZero,
  ended,
  finalized,
  tournament,
  countdownTo
}) {
  if (!tournament.userInGroup && !ended) {
    return (
      <div className="tournament-message">
        <img
          className="fill-image"
          src={ExclusiveCoverImg}
          alt="This is an exclusive tournament that is limited to a specific group of players."
        />
      </div>
    );
  } else {
    const upcoming = tournament.status === TOURNAMENT_STATUS_UPCOMING;
    let title = 'Tournament';
    let content = null;
    if (finalized) {
      const places =
        tournament &&
        tournament.winningPlaces &&
        tournament.winningPlaces.winningPlaces &&
        tournament.winningPlaces.winningPlaces.map(p => p.place);
      title = 'Final Rankings';
      content = (
        <div className="leaderboard-rankings">
          {rankings.map((element, index) => {
            return (
              <Ranking
                key={index}
                rank={element.rank}
                player={element.player}
                score={element.score}
                places={places}
                display={DISPLAY_FINAL}
              />
            );
          })}
        </div>
      );
    } else if (ended) {
      title = 'Final Rankings';
      content = (
        <div className="message-description">
          <A11yFontIcon icon="spinner" size="3x" pulse fixed alt="" />
          <span>Retrieving scores...</span>
        </div>
      );
    } else if (balanceZero) {
      title = 'Out of Credits';
      content = (
        <div className="message-description">
          <div>Out of Credits</div>
          <div>You have played all of your credits.</div>
          <div>
            This tournament provides a fixed number of credits. Once they have
            been played, you cannot reload your balance.
          </div>
          <div>
            Please wait for the tournament to end to view the final results.
          </div>
        </div>
      );
    } else if (upcoming) {
      title = 'Upcoming Tournament';
      content = (
        <div className="message-description">
          <div>Tournament will begin in:</div>
          <Countdown date={countdownTo} renderer={countdownRenderer} />
        </div>
      );
    }

    return (
      <div className="tournament-message">
        <div className="leaderboard-title">{title}</div>

        <div className="tournament-info">
          <div className="tournament-name">{tournament.name}</div>
          <div className="tournament-image">
            <GameIcon
              game={tournament.game}
              alt={tournament.game.displayName}
              badge={tournament.game.badge}
            />
          </div>
          <div className="tournament-description">{tournament.description}</div>
          {ended && (
            <div className="tournament-completed">
              This tournament has completed. Join us next time and find your
              winning moment.
            </div>
          )}
        </div>
        {content}
      </div>
    );
  }
}

TournamentMessage.propTypes = {
  rankings: PropTypes.array.isRequired,
  tournament: PropTypes.object.isRequired,
  finalized: PropTypes.bool
};
